import React, { useState, useEffect, useMemo } from "react";
import { 
  Wallet, 
  Download, 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  ShieldCheck, 
  RefreshCw, 
  Calendar, 
  Briefcase, 
  Layers, 
  Building, 
  FileText, 
  ExternalLink,
  ChevronRight,
  X,
  CreditCard,
  UserCheck,
  TrendingUp
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { 
  fetchStaffFinanceOverview, 
  StaffFinanceOverview, 
  StaffDeliverableItem, 
  DynamicServiceBreakdown 
} from "../../services/paymentFinanceService";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const AdminPayments: React.FC = () => {
  const currentMonthStr = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthStr);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [overview, setOverview] = useState<StaffFinanceOverview | null>(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [serviceFilter, setServiceFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  // Itemized Deliverable Slip Modal
  const [selectedDeliverable, setSelectedDeliverable] = useState<StaffDeliverableItem | null>(null);

  const loadFinanceData = async (month?: string) => {
    try {
      setLoading(true);
      const data = await fetchStaffFinanceOverview(month || selectedMonth);
      setOverview(data);
    } catch (err) {
      console.error("Failed to load staff finance overview:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadFinanceData(selectedMonth);
  }, [selectedMonth]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadFinanceData(selectedMonth);
  };

  // Filtered Itemized Deliverables
  const filteredDeliverables = useMemo(() => {
    if (!overview?.itemized_deliverables) return [];
    return overview.itemized_deliverables.filter((item) => {
      const matchesSearch = 
        item.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.service_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(item.id).includes(searchQuery);

      const matchesService = serviceFilter === "ALL" || item.service_type === serviceFilter;

      let matchesStatus = true;
      if (statusFilter === "APPROVED") matchesStatus = item.is_approved;
      if (statusFilter === "AWAITING") matchesStatus = !item.is_approved;
      if (statusFilter === "PAID") matchesStatus = item.is_commission_paid;
      if (statusFilter === "QUEUED") matchesStatus = item.is_approved && !item.is_commission_paid;

      return matchesSearch && matchesService && matchesStatus;
    });
  }, [overview, searchQuery, serviceFilter, statusFilter]);

  const availableMonths = overview?.available_months || [currentMonthStr];

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-3 cht-py-1 cht-bg-blue-50 cht-text-[#3e57da] cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-rounded-xl">
              Specialist Portal
            </span>
            <span className="cht-text-xs cht-font-bold cht-text-gray-400">
              Period: {selectedMonth}
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
            Personal Finance & Payouts
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Track your approved service commissions, dynamic service lines, and monthly payroll disbursements.
          </p>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          {/* Month Selector */}
          <div className="cht-w-44">
            <Input
              as="select"
              value={selectedMonth}
              onChange={(e: any) => setSelectedMonth(e.target.value)}
              options={availableMonths.map((m) => ({
                label: new Date(`${m}-01`).toLocaleDateString("en-US", { month: "long", year: "numeric" }),
                value: m,
              }))}
            />
          </div>

          <Button
            variant="outline"
            onClick={handleRefresh}
            leftIcon={<RefreshCw size={16} className={refreshing ? "cht-animate-spin" : ""} />}
            className="cht-rounded-xl"
            title="Refresh financial data"
          >
            Refresh
          </Button>
        </div>
      </header>

      {/* KPI Deck */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-5">
        {/* Ready for Payout */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl">
            <Wallet size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Ready for Payout
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(overview?.pending_payout || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-indigo-600">
              Approved for {selectedMonth}
            </span>
          </div>
        </GlassCard>

        {/* Awaiting Approval */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl">
            <Clock size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Awaiting Review
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              {overview?.awaiting_approval_count || 0} Deliverables
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-amber-600">
              Pending SuperAdmin review
            </span>
          </div>
        </GlassCard>

        {/* Disbursed this Month */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Paid Out ({selectedMonth})
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(overview?.total_paid || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-emerald-600">
              Disbursed to date
            </span>
          </div>
        </GlassCard>

        {/* Cumulative Historical Payout */}
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Lifetime Paid Out
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900">
              ₦{Number(overview?.lifetime_paid || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </h4>
            <span className="cht-text-[11px] cht-font-medium cht-text-purple-600">
              Total career disbursements
            </span>
          </div>
        </GlassCard>
      </div>

      {/* Dynamic Service Breakdown */}
      <div className="cht-space-y-3">
        <div className="cht-flex cht-items-center cht-justify-between">
          <div className="cht-flex cht-items-center cht-gap-2">
            <Layers size={18} className="cht-text-[#3e57da]" />
            <h3 className="cht-text-base cht-font-black cht-text-gray-900">
              Earnings by Feature Service ({selectedMonth})
            </h3>
          </div>
          <span className="cht-text-xs cht-font-bold cht-text-gray-400">
            {overview?.service_breakdown?.length || 0} Active Service Lines
          </span>
        </div>

        {overview?.service_breakdown && overview.service_breakdown.length > 0 ? (
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-4">
            {overview.service_breakdown.map((sb: DynamicServiceBreakdown) => (
              <GlassCard
                key={sb.service_code}
                className="cht-p-5 cht-rounded-[24px] cht-border-l-4 cht-border-l-[#3e57da] hover:cht-shadow-md cht-transition-shadow"
              >
                <div className="cht-flex cht-items-start cht-justify-between cht-gap-2">
                  <div>
                    <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                      {sb.service_code}
                    </span>
                    <h5 className="cht-text-sm cht-font-black cht-text-gray-800 cht-truncate cht-max-w-[180px]">
                      {sb.service_name}
                    </h5>
                  </div>
                  <span className="cht-px-2 cht-py-0.5 cht-bg-blue-50 cht-text-blue-600 cht-text-[10px] cht-font-black cht-rounded-lg">
                    {sb.count} items
                  </span>
                </div>

                <div className="cht-mt-4 cht-pt-3 cht-border-t cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
                  <div>
                    <span className="cht-text-[10px] cht-text-gray-400 cht-font-bold cht-block">Approved Cut</span>
                    <span className="cht-text-sm cht-font-black cht-text-emerald-600">
                      ₦{Number(sb.total_commission || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="cht-text-right">
                    <span className="cht-text-[10px] cht-text-gray-400 cht-font-bold cht-block">Pending Payout</span>
                    <span className="cht-text-sm cht-font-black cht-text-indigo-600">
                      ₦{Number(sb.pending_commission || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        ) : (
          <GlassCard className="cht-p-8 cht-rounded-[24px] cht-text-center cht-text-gray-400">
            <p className="cht-text-sm cht-font-medium">No service deliverables logged for {selectedMonth} yet.</p>
          </GlassCard>
        )}
      </div>

      {/* Filter & Search Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Input
            leftIcon={<Search size={18} />}
            placeholder="Search deliverables by customer or service..."
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          {/* Dynamic Service Filter */}
          <div className="cht-w-44">
            <Input
              as="select"
              value={serviceFilter}
              onChange={(e: any) => setServiceFilter(e.target.value)}
              options={[
                { label: "All Services", value: "ALL" },
                ...(overview?.service_breakdown || []).map((sb) => ({
                  label: sb.service_name,
                  value: sb.service_code,
                })),
              ]}
            />
          </div>

          {/* Status Filter */}
          <div className="cht-w-40">
            <Input
              as="select"
              value={statusFilter}
              onChange={(e: any) => setStatusFilter(e.target.value)}
              options={[
                { label: "All Statuses", value: "ALL" },
                { label: "Queued for Payout", value: "QUEUED" },
                { label: "Approved", value: "APPROVED" },
                { label: "Awaiting Review", value: "AWAITING" },
                { label: "Paid Out", value: "PAID" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Monthly Itemized Deliverables Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        <div className="cht-p-5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
          <div>
            <h4 className="cht-text-base cht-font-black cht-text-gray-900">
              Itemized Deliverables & Settlement Slips
            </h4>
            <p className="cht-text-xs cht-font-medium cht-text-gray-400">
              Individual fulfillments contributing to your {selectedMonth} commission payout.
            </p>
          </div>
          <span className="cht-text-xs cht-font-bold cht-text-gray-400">
            Showing {filteredDeliverables.length} records
          </span>
        </div>

        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-text-left cht-border-collapse">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                <th className="cht-py-4 cht-px-6">Ref / Date</th>
                <th className="cht-py-4 cht-px-6">Customer Name</th>
                <th className="cht-py-4 cht-px-6">Feature Service</th>
                <th className="cht-py-4 cht-px-6">Amount Billed</th>
                <th className="cht-py-4 cht-px-6">Your Cut</th>
                <th className="cht-py-4 cht-px-6">Approval</th>
                <th className="cht-py-4 cht-px-6">Payout Status</th>
                <th className="cht-py-4 cht-px-6 cht-text-right">Action</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredDeliverables.length > 0 ? (
                filteredDeliverables.map((item) => (
                  <tr key={item.id} className="hover:cht-bg-gray-50/50 cht-transition-colors">
                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-black cht-text-gray-900">#{item.id}</span>
                      <p className="cht-text-[10px] cht-font-bold cht-text-gray-400">{item.date || "N/A"}</p>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <p className="cht-text-xs cht-font-black cht-text-gray-800">{item.customer_name}</p>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <div className="cht-flex cht-items-center cht-gap-2">
                        <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-blue-50 cht-text-blue-700 cht-text-[11px] cht-font-black">
                          {item.service_name}
                        </span>
                        {item.is_split && (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-amber-50 cht-text-amber-700 cht-text-[10px] cht-font-black cht-border cht-border-amber-200">
                            {item.split_percentage ? `${item.split_percentage}% Cut` : "Split"}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-bold cht-text-gray-700">
                        ₦{Number(item.amount_paid || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-black cht-text-emerald-600">
                        ₦{Number(item.commission || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {item.is_approved ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-emerald-50 cht-text-emerald-700 cht-text-[10px] cht-font-black">
                          <CheckCircle2 size={11} />
                          Approved
                        </span>
                      ) : (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-amber-50 cht-text-amber-700 cht-text-[10px] cht-font-black">
                          <Clock size={11} />
                          Awaiting Review
                        </span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {item.is_commission_paid ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-purple-50 cht-text-purple-700 cht-text-[10px] cht-font-black">
                          <CheckCircle2 size={11} />
                          Disbursed
                        </span>
                      ) : item.is_approved ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-indigo-50 cht-text-indigo-700 cht-text-[10px] cht-font-black">
                          <Clock size={11} />
                          Ready for Payout
                        </span>
                      ) : (
                        <span className="cht-text-xs cht-text-gray-400 cht-font-medium">—</span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6 cht-text-right">
                      <button
                        type="button"
                        onClick={() => setSelectedDeliverable(item)}
                        className="cht-text-xs cht-font-bold cht-text-[#3e57da] hover:cht-underline cht-inline-flex cht-items-center cht-gap-1"
                      >
                        View Slip
                        <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="cht-py-12 cht-text-center cht-text-gray-400">
                    <p className="cht-text-sm cht-font-medium">No deliverables found matching current filters.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Verified Payout Account Card */}
      <GlassCard className="cht-p-6 cht-rounded-[28px] cht-border-l-4 cht-border-l-emerald-500 cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <Building size={24} />
          </div>
          <div>
            <h5 className="cht-text-sm cht-font-black cht-text-gray-900">
              Verified Settlement Account
            </h5>
            <p className="cht-text-xs cht-font-medium cht-text-gray-500">
              Monthly approved payouts are automatically processed to your registered payroll profile.
            </p>
          </div>
        </div>
        <span className="cht-px-3 cht-py-1 cht-bg-emerald-50 cht-text-emerald-700 cht-text-[11px] cht-font-black cht-rounded-xl">
          Active for {selectedMonth} Payroll
        </span>
      </GlassCard>

      {/* Itemized Deliverable Slip Modal */}
      {selectedDeliverable && (
        <Modal
          isOpen={Boolean(selectedDeliverable)}
          onClose={() => setSelectedDeliverable(null)}
          title={`Settlement Slip #${selectedDeliverable.id}`}
        >
          <div className="cht-space-y-5 cht-p-2">
            <div className="cht-bg-gray-50 cht-p-4 cht-rounded-2xl cht-space-y-3">
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Customer / Client</span>
                <span className="cht-font-black cht-text-gray-900">{selectedDeliverable.customer_name}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Feature Service</span>
                <span className="cht-font-black cht-text-[#3e57da]">{selectedDeliverable.service_name}</span>
              </div>
              {selectedDeliverable.is_split && (
                <div className="cht-flex cht-justify-between cht-items-center cht-text-xs cht-p-2.5 cht-bg-amber-50/80 cht-rounded-xl cht-border cht-border-amber-200">
                  <span className="cht-text-amber-800 cht-font-bold">Commission Split Allocation</span>
                  <span className="cht-font-black cht-text-amber-900 cht-px-2 cht-py-0.5 cht-bg-amber-100 cht-rounded-md">
                    {selectedDeliverable.split_percentage}% Cut ({selectedDeliverable.stage_status?.replace('_', ' ') || 'SPLIT'})
                  </span>
                </div>
              )}
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Fulfillment Date</span>
                <span className="cht-font-bold cht-text-gray-700">{selectedDeliverable.date || "N/A"}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Amount Billed to Client</span>
                <span className="cht-font-black cht-text-gray-900">
                  ₦{Number(selectedDeliverable.amount_paid || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Commission Breakdown */}
            <div className="cht-p-4 cht-border cht-border-emerald-100 cht-bg-emerald-50/50 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
              <div>
                <span className="cht-text-[10px] cht-font-black cht-text-emerald-800 cht-uppercase cht-tracking-wider">
                  Specialist Commission
                </span>
                <h4 className="cht-text-2xl cht-font-black cht-text-emerald-700">
                  ₦{Number(selectedDeliverable.commission || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </h4>
              </div>
              <div className="cht-text-right">
                <span className={`cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black ${
                  selectedDeliverable.is_commission_paid
                    ? "cht-bg-purple-100 cht-text-purple-800"
                    : selectedDeliverable.is_approved
                    ? "cht-bg-indigo-100 cht-text-indigo-800"
                    : "cht-bg-amber-100 cht-text-amber-800"
                }`}>
                  {selectedDeliverable.is_commission_paid
                    ? "Disbursed"
                    : selectedDeliverable.is_approved
                    ? "Ready for Payout"
                    : "Awaiting Review"}
                </span>
              </div>
            </div>

            <div className="cht-flex cht-justify-end cht-pt-2">
              <Button
                variant="outline"
                onClick={() => setSelectedDeliverable(null)}
                className="cht-rounded-xl"
              >
                Close Slip
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPayments;

